import type { ComponentType } from 'react'
import { Monitor, RotateCw, Smartphone } from 'lucide-react'
import { cn } from '@teach/ui'
import type { PreviewViewport } from '@/features/editor/components/blocks/previews/previewViewports'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  value: PreviewViewport
  onChange: (next: PreviewViewport) => void
}

/** Сегмент в стиле `SegmentedControl` дизайн-системы. */
const segmentClass = (selected: boolean) =>
  cn(
    'flex h-9 items-center gap-1.5 rounded-full px-4 text-sm font-medium transition-[color,background-color,padding] duration-300 outline-none focus-visible:ring-2 focus-visible:ring-ring',
    selected
      ? 'bg-foreground/15 text-foreground shadow-sm'
      : 'text-muted-foreground hover:text-foreground',
  )

const Label = ({
  icon: Icon,
  text,
}: {
  icon: ComponentType<{ className?: string }>
  text: string
}) => (
  <>
    <Icon className="size-3.5" />
    {text}
  </>
)

/**
 * Как автор смотрит горизонтальный ролик на холсте: с телефона (ролик целиком,
 * поля сверху и снизу), с повёрнутого телефона или с ПК (широкий экран).
 * «Повернуть» живёт внутри плашки «Телефон», справа, — только когда она выбрана.
 * Вложить кнопку в кнопку `SegmentedControl` нельзя, поэтому плашка своя, но
 * классы — те же, что у сегментов дизайн-системы.
 */
export const VideoViewportSwitch = ({ value, onChange }: Props) => {
  const m = useMessages(messages)
  const phone = value !== 'desktop'
  return (
    <div
      role="tablist"
      // Стеклянная, как подложка инструментов под ней.
      className="inline-flex items-center rounded-full border border-white/10 bg-background/40 p-1 shadow-lg backdrop-blur-xl backdrop-saturate-150"
    >
      <button
        type="button"
        role="tab"
        aria-selected={!phone}
        className={segmentClass(!phone)}
        onClick={() => onChange('desktop')}
      >
        <Label icon={Monitor} text={m.desktop} />
      </button>
      <div className={cn(segmentClass(phone), phone ? 'pr-1.5' : null)}>
        <button
          type="button"
          role="tab"
          aria-selected={phone}
          className="flex items-center gap-1.5 outline-none"
          onClick={() => onChange('phone')}
        >
          <Label icon={Smartphone} text={m.phone} />
        </button>
        {/* Кружок поворота всегда в разметке: на выборе телефона плавно
            выезжает (ширина + прозрачность), на ПК так же плавно уходит. */}
        <span
          className={cn(
            'flex overflow-hidden transition-[max-width,opacity,margin] duration-300 ease-out',
            phone ? 'ml-1 max-w-8 opacity-100' : 'ml-0 max-w-0 opacity-0',
          )}
        >
          <button
            type="button"
            aria-label={m.rotatePhone}
            title={m.rotatePhone}
            aria-pressed={value === 'phoneLandscape'}
            aria-hidden={!phone}
            tabIndex={phone ? 0 : -1}
            onClick={() => onChange(value === 'phoneLandscape' ? 'phone' : 'phoneLandscape')}
            className="flex size-7 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground"
          >
            <RotateCw className="size-3.5" />
          </button>
        </span>
      </div>
    </div>
  )
}
